/**
 * Entrada rapida em linguagem natural, pt-BR (spec 10.1).
 *
 * "Enviar OAP amanha 14h !alta #protecao *Regulatorio"
 *   -> titulo "Enviar OAP", due_at amanha as 14:00, prioridade alta,
 *      tag "protecao", lista "Regulatorio"
 *
 * A spec chama isto de "a funcionalidade que mais reduz atrito" e manda
 * prioriza-la. Duas decisoes definem o arquivo:
 *
 * 1. **Funcao pura, com `now` e `timeZone` injetados.** "Amanha as 14h" depende
 *    de quando e onde a pessoa digitou. Ler o relogio aqui dentro tornaria o
 *    teste nao deterministico exatamente nos casos que mais importam — virada
 *    de dia, de mes e horario de verao.
 * 2. **Devolve o que consumiu (`matches`), nao so o resultado.** A spec exige
 *    "preview do que foi entendido antes de salvar": sem os trechos casados, a
 *    UI teria de adivinhar o que o parser fez, e um palpite errado no preview e
 *    pior que preview nenhum.
 *
 * O que NAO e interpretado, deliberadamente: duracao ("por 2h"), recorrencia
 * ("toda terca") e lembrete ("avisar 30min antes"). Cada um tem sintaxe ambigua
 * em pt-BR e um campo proprio na tela de detalhe; adivinhar errado num campo
 * que o usuario nao esta vendo e pior do que nao adivinhar.
 */

import { PRIORITY, type Priority } from './constants.js';
import {
  addDaysInZone,
  endOfDayInZone,
  partsInZone,
  startOfDayInZone,
  zonedPartsToUtc,
} from './datetime.js';

export type MatchKind = 'date' | 'time' | 'priority' | 'tag' | 'list';

export interface ParsedMatch {
  kind: MatchKind;
  /** Trecho literal consumido da entrada, para o preview. */
  text: string;
  /** Rotulo em pt-BR do que aquilo virou. */
  label: string;
}

export interface ParsedQuickAdd {
  /** O que sobrou depois de remover tudo que foi interpretado. */
  title: string;
  dueAt: number | null;
  /** true quando ha data mas nao ha hora — nao inventa "00:00". */
  allDay: boolean;
  priority: Priority;
  /** Nomes, nao ids: quem resolve para id e o cliente, que conhece as tags. */
  tagNames: string[];
  listName: string | null;
  matches: ParsedMatch[];
}

// ---------------------------------------------------------------- normalizacao

/**
 * Acentuadas para o equivalente sem acento, UM PARA UM.
 *
 * `normalize('NFD')` seria o caminho obvio e esta errado aqui: ele decompoe "a"
 * acentuado em dois code points, os indices deslocam, e todo trecho casado
 * passaria a apontar para o lugar errado da string original. A substituicao 1:1
 * preserva o comprimento, que e o que permite recortar o titulo por indice.
 */
const SEM_ACENTO: Record<string, string> = {
  á: 'a',
  à: 'a',
  â: 'a',
  ã: 'a',
  ä: 'a',
  é: 'e',
  è: 'e',
  ê: 'e',
  ë: 'e',
  í: 'i',
  ì: 'i',
  î: 'i',
  ï: 'i',
  ó: 'o',
  ò: 'o',
  ô: 'o',
  õ: 'o',
  ö: 'o',
  ú: 'u',
  ù: 'u',
  û: 'u',
  ü: 'u',
  ç: 'c',
  ñ: 'n',
};

function normalizar(texto: string): string {
  let saida = '';
  for (const ch of texto.toLowerCase()) saida += SEM_ACENTO[ch] ?? ch;
  return saida;
}

// ------------------------------------------------------------------- tabelas

const DIAS_DA_SEMANA: Record<string, number> = {
  domingo: 0,
  segunda: 1,
  terca: 2,
  quarta: 3,
  quinta: 4,
  sexta: 5,
  sabado: 6,
};

const NOME_DO_DIA_PT: Record<number, string> = {
  0: 'domingo',
  1: 'segunda-feira',
  2: 'terça-feira',
  3: 'quarta-feira',
  4: 'quinta-feira',
  5: 'sexta-feira',
  6: 'sábado',
};

const PRIORIDADE_POR_PALAVRA: Record<string, Priority> = {
  alta: PRIORITY.HIGH,
  media: PRIORITY.MEDIUM,
  baixa: PRIORITY.LOW,
  nenhuma: PRIORITY.NONE,
  '3': PRIORITY.HIGH,
  '2': PRIORITY.MEDIUM,
  '1': PRIORITY.LOW,
  '0': PRIORITY.NONE,
};

const ROTULO_PRIORIDADE_PT: Record<Priority, string> = {
  [PRIORITY.NONE]: 'sem prioridade',
  [PRIORITY.LOW]: 'prioridade baixa',
  [PRIORITY.MEDIUM]: 'prioridade média',
  [PRIORITY.HIGH]: 'prioridade alta',
};

/** Intervalo ja consumido da entrada. */
interface Faixa {
  inicio: number;
  fim: number;
}

function colide(faixas: Faixa[], inicio: number, fim: number): boolean {
  return faixas.some((f) => inicio < f.fim && fim > f.inicio);
}

/**
 * Ordena candidatos pela posicao na entrada: vence o mais a ESQUERDA e, em caso
 * de empate, o mais LONGO.
 *
 * A posicao e o unico criterio que o usuario consegue prever — foi ele quem
 * escolheu a ordem das palavras. O desempate por comprimento e o que faz
 * "depois de amanha" ganhar do "amanha" contido nele.
 */
function ordenarPorPosicao(candidatos: Faixa[]): void {
  candidatos.sort(
    (a, b) => a.inicio - b.inicio || (b.fim - b.inicio) - (a.fim - a.inicio),
  );
}

function formatarData(timeZone: string, epochMs: number): string {
  const p = partsInZone(timeZone, epochMs);
  return `${String(p.day).padStart(2, '0')}/${String(p.month).padStart(2, '0')}`;
}

function formatarHora(timeZone: string, epochMs: number): string {
  const p = partsInZone(timeZone, epochMs);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

export interface ParseOptions {
  now: number;
  timeZone: string;
}

/**
 * Interpreta a entrada rapida.
 *
 * A ordem das buscas nao e cosmetica: "depois de amanha" precisa ser tentado
 * antes de "amanha", e "proxima semana" antes dos dias da semana — senao o
 * padrao mais curto casa primeiro e engole metade da expressao, deixando um
 * "depois de" solto no titulo.
 */
export function parseQuickAdd(entrada: string, options: ParseOptions): ParsedQuickAdd {
  const { now, timeZone } = options;
  const norm = normalizar(entrada);

  const consumidas: Faixa[] = [];
  const matches: ParsedMatch[] = [];

  let priority: Priority = PRIORITY.NONE;
  const tagNames: string[] = [];
  let listName: string | null = null;

  /** Dia civil escolhido, ainda sem hora. */
  let diaBase: number | null = null;
  let horaEscolhida: { hour: number; minute: number } | null = null;

  const registrar = (inicio: number, fim: number, kind: MatchKind, label: string): boolean => {
    if (colide(consumidas, inicio, fim)) return false;
    consumidas.push({ inicio, fim });
    matches.push({ kind, text: entrada.slice(inicio, fim).trim(), label });
    return true;
  };

  // ------------------------------------------------------------- prioridade
  for (const m of norm.matchAll(/(^|\s)!(alta|media|baixa|nenhuma|[0-3])\b/g)) {
    const prefixo = m[1] ?? '';
    const inicio = m.index + prefixo.length;
    const valor = PRIORIDADE_POR_PALAVRA[m[2] ?? ''];
    if (valor === undefined) continue;

    const fim = m.index + m[0].length;
    if (registrar(inicio, fim, 'priority', ROTULO_PRIORIDADE_PT[valor])) priority = valor;
  }

  // -------------------------------------------------------------------- tags
  // `\p{L}` cobre acentuadas: `#proteção` vira a tag "proteção" COM acento —
  // quem escreve a tag e quem decide como ela se chama.
  for (const m of entrada.matchAll(/(^|\s)#([\p{L}\p{N}_-]+)/gu)) {
    const prefixo = m[1] ?? '';
    const inicio = m.index + prefixo.length;
    const nome = m[2] ?? '';
    if (registrar(inicio, inicio + 1 + nome.length, 'tag', `tag ${nome}`)) tagNames.push(nome);
  }

  // ------------------------------------------------------------------- lista
  for (const m of entrada.matchAll(/(^|\s)\*([\p{L}\p{N}_-]+)/gu)) {
    const prefixo = m[1] ?? '';
    const inicio = m.index + prefixo.length;
    const nome = m[2] ?? '';
    if (registrar(inicio, inicio + 1 + nome.length, 'list', `lista ${nome}`)) {
      listName ??= nome;
    }
  }

  // -------------------------------------------------------------------- data
  const hoje = startOfDayInZone(timeZone, now);

  /**
   * Candidatos a data, de todos os padroes.
   *
   * Coletar tudo antes de escolher, em vez de aceitar o primeiro padrao que
   * casa, e o que torna o resultado previsivel: quem escreve "hoje amanha"
   * espera "hoje", porque foi o que digitou primeiro. Resolver na ordem dos
   * PADROES faria vencer o que estivesse mais acima neste arquivo — uma regra
   * que o usuario nao tem como adivinhar.
   */
  const candidatosData: Array<{ inicio: number; fim: number; valor: number }> = [];

  const coletarDatas = (
    regex: RegExp,
    resolver: (m: RegExpMatchArray) => number | null,
  ): void => {
    for (const m of norm.matchAll(regex)) {
      const prefixo = m[1] ?? '';
      const resolvido = resolver(m);
      if (resolvido === null) continue;

      candidatosData.push({
        inicio: m.index + prefixo.length,
        fim: m.index + m[0].length,
        valor: resolvido,
      });
    }
  };

  coletarDatas(/(^|\s)depois de amanha\b/g, () => addDaysInZone(timeZone, hoje, 2));
  coletarDatas(/(^|\s)amanha\b/g, () => addDaysInZone(timeZone, hoje, 1));
  coletarDatas(/(^|\s)hoje\b/g, () => hoje);

  coletarDatas(/(^|\s)em (\d{1,3}) (dias?|semanas?)\b/g, (m) => {
    const quantidade = Number(m[2]);
    if (!Number.isFinite(quantidade)) return null;
    const passo = (m[3] ?? '').startsWith('semana') ? 7 : 1;
    return addDaysInZone(timeZone, hoje, quantidade * passo);
  });

  coletarDatas(/(^|\s)(proxima semana|semana que vem)\b/g, () =>
    addDaysInZone(timeZone, hoje, 7),
  );

  // Dia da semana. "proxima"/"proximo" e opcional e nao muda nada: a regra ja e
  // sempre a proxima ocorrencia FUTURA.
  coletarDatas(
    /(^|\s)(?:proxima |proximo )?(domingo|segunda|terca|quarta|quinta|sexta|sabado)(?:-?\s?feira)?\b/g,
    (m) => {
      const alvo = DIAS_DA_SEMANA[m[2] ?? ''];
      if (alvo === undefined) return null;

      const atual = partsInZone(timeZone, hoje).weekday;
      // `|| 7`: dizer "segunda" numa segunda-feira significa a semana que vem,
      // nao "daqui a zero dias".
      const avanco = ((alvo - atual + 7) % 7) || 7;
      return addDaysInZone(timeZone, hoje, avanco);
    },
  );

  // Data absoluta: 21/07 ou 21/07/2026.
  coletarDatas(/(^|\s)(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/g, (m) => {
    const dia = Number(m[2]);
    const mes = Number(m[3]);
    if (dia < 1 || dia > 31 || mes < 1 || mes > 12) return null;

    const partes = partsInZone(timeZone, now);
    const anoBruto = m[4] === undefined ? partes.year : Number(m[4]);
    // "21/07/26" e 2026, nao o ano 26.
    const ano = anoBruto < 100 ? 2000 + anoBruto : anoBruto;

    const candidato = zonedPartsToUtc(timeZone, { year: ano, month: mes, day: dia });

    // Sem ano explicito e com a data ja passada, vale o ano que vem: ninguem
    // agenda para tras ao escrever "02/01" em dezembro.
    if (m[4] === undefined && candidato < hoje) {
      return zonedPartsToUtc(timeZone, { year: ano + 1, month: mes, day: dia });
    }
    return candidato;
  });

  ordenarPorPosicao(candidatosData);

  for (const c of candidatosData) {
    if (registrar(c.inicio, c.fim, 'date', formatarData(timeZone, c.valor))) {
      diaBase = c.valor;
      break;
    }
  }

  // -------------------------------------------------------------------- hora
  // Mesma coleta-depois-escolhe das datas, pelo mesmo motivo: com os padroes
  // aplicados em sequencia, "14:30 e 9h" escolheria 9h so porque o padrao com
  // `h` e tentado antes do padrao com `:`.
  const candidatosHora: Array<{ inicio: number; fim: number; hour: number; minute: number }> =
    [];

  const coletarHoras = (regex: RegExp): void => {
    for (const m of norm.matchAll(regex)) {
      const prefixo = m[1] ?? '';
      const hour = Number(m[2]);
      const minute = m[3] === undefined ? 0 : Number(m[3]);
      if (hour > 23 || minute > 59) continue;

      candidatosHora.push({
        inicio: m.index + prefixo.length,
        fim: m.index + m[0].length,
        hour,
        minute,
      });
    }
  };

  // "as 14h", "14h", "14h30"
  coletarHoras(/(^|\s)(?:as\s+)?(\d{1,2})h(\d{2})?\b/g);
  // "14:30" — exige os minutos, senao "14:" nao significa nada.
  coletarHoras(/(^|\s)(?:as\s+)?(\d{1,2}):(\d{2})\b/g);

  ordenarPorPosicao(candidatosHora);

  for (const c of candidatosHora) {
    const rotulo = `${String(c.hour).padStart(2, '0')}:${String(c.minute).padStart(2, '0')}`;
    if (registrar(c.inicio, c.fim, 'time', rotulo)) {
      horaEscolhida = { hour: c.hour, minute: c.minute };
      break;
    }
  }

  // --------------------------------------------------------------- resultado
  let dueAt: number | null = null;
  let allDay = false;

  if (horaEscolhida !== null) {
    const hora: { hour: number; minute: number } = horaEscolhida;
    const dia = diaBase ?? hoje;
    const p = partsInZone(timeZone, dia);

    dueAt = zonedPartsToUtc(timeZone, {
      year: p.year,
      month: p.month,
      day: p.day,
      hour: hora.hour,
      minute: hora.minute,
    });

    // Hora sem data que ja passou vai para amanha. Digitar "14h" as 18h e um
    // pedido para o dia seguinte, nao uma tarefa que nasce vencida.
    if (diaBase === null && dueAt <= now) {
      dueAt = addDaysInZone(timeZone, dueAt, 1);
    }
  } else if (diaBase !== null) {
    // Data sem hora: vence no FIM do dia civil, para a tarefa continuar em
    // "hoje" o dia inteiro em vez de nascer vencida a 00:00.
    dueAt = endOfDayInZone(timeZone, diaBase);
    allDay = true;
  }

  // Os rotulos de data e hora sao reescritos no fim porque a hora pode ter
  // empurrado o dia (o caso do "14h" digitado as 18h).
  if (dueAt !== null) {
    for (const match of matches) {
      if (match.kind === 'date') match.label = formatarData(timeZone, dueAt);
      if (match.kind === 'time') match.label = formatarHora(timeZone, dueAt);
    }
  }

  // ------------------------------------------------------------------ titulo
  const ordenadas = [...consumidas].sort((a, b) => a.inicio - b.inicio);
  let titulo = '';
  let cursor = 0;
  for (const faixa of ordenadas) {
    titulo += entrada.slice(cursor, faixa.inicio);
    cursor = faixa.fim;
  }
  titulo += entrada.slice(cursor);

  return {
    title: titulo.replace(/\s+/g, ' ').trim(),
    dueAt,
    allDay,
    priority,
    tagNames,
    listName,
    matches,
  };
}

/** Nome do dia da semana em pt-BR, para o preview. */
export function weekdayNamePt(timeZone: string, epochMs: number): string {
  return NOME_DO_DIA_PT[partsInZone(timeZone, epochMs).weekday] ?? '';
}
